import React, { useMemo } from 'react';
import { T, useTranslate } from '@tolgee/react';
import { AnimatePresence, motion } from 'framer-motion';
import { TrackingTruck } from '../orderTracking.interface';
import {
	buildStepViews,
	currentStepIndex,
	formatDuration,
	formatTime,
	isTruckAssigned,
	isTruckComplete,
	minutesSince,
	reachedStepsCount,
	truckIdentity,
} from '../orderTracking.utils';
import { TRACKING_STEPS } from '../orderTracking.constants';
import { cn } from '../../../../lib/utils';
import { TRACKING_DRAWER_TRANSITION } from '../orderTracking.constants';
import TruckSteps from './TruckSteps';
import { ChevronDownIcon } from '../../../../assets/ChevronDownIcon';

interface TruckRowProps {
	truck: TrackingTruck;
	expanded: boolean;
	onToggle: (truckId: number) => void;
	collapsible?: boolean;
	onOpenDocuments?: (truckId: number) => void;
}

const TruckRow: React.FC<TruckRowProps> = ({
	truck,
	expanded,
	onToggle,
	collapsible = true,
	onOpenDocuments,
}) => {
	const { t } = useTranslate();

	const reached = reachedStepsCount(truck);
	const complete = isTruckComplete(truck);
	const current = currentStepIndex(truck);
	const moving = truck.speedKmh > 0;
	const idle = reached === 0;
	const assigned = isTruckAssigned(truck);
	// Without a truck there is no route to show, so the row can't be selected
	const selectable = collapsible && assigned;
	const isOpen = collapsible ? expanded && assigned : true;

	const steps = useMemo(() => buildStepViews(truck, t), [truck, t]);

	const summary = idle
		? t('orderTracking.notYetAccepted', 'Not yet accepted')
		: complete
		? t(
				TRACKING_STEPS[TRACKING_STEPS.length - 1].labelKey,
				TRACKING_STEPS[TRACKING_STEPS.length - 1].defaultLabel,
		  )
		: t(TRACKING_STEPS[current].labelKey, TRACKING_STEPS[current].defaultLabel);

	const shortStatus = idle
		? t('orderTracking.inactive', 'Inactive')
		: complete
		? t('orderTracking.deliveryCompleted', 'Delivery completed')
		: truck.stoppedSince
		? t('orderTracking.stoppedFor', 'Stopped {time}', {
				time: formatDuration(minutesSince(truck.stoppedSince)),
		  })
		: truck.lastSeenAt
		? t('orderTracking.lastSeen', 'Last seen {time}', {
				time: formatTime(truck.lastSeenAt),
		  })
		: summary;

	const liveDeliveryEta =
		truck.liveEtaTo === 'delivery' ? truck.liveEta : undefined;
	const showEta = !complete && !idle && !!(liveDeliveryEta || truck.eta);

	return (
		<div className="border-0 border-t border-solid border-track-surface flex flex-col">
			<button
				type="button"
				onClick={() => selectable && onToggle(truck.id)}
				disabled={collapsible && !assigned}
				className={cn(
					'w-full text-left flex flex-col gap-3 px-0 py-[14px] bg-transparent border-none font-[inherit]',
					selectable
						? 'cursor-pointer'
						: collapsible
						? 'cursor-not-allowed'
						: 'cursor-default',
				)}
			>
				<div className="flex items-center gap-[14px]">
					<div
						className={cn(
							'w-11 h-11 box-border rounded-[32%] flex items-center justify-center font-bold text-[15px] flex-none',
							assigned
								? 'bg-track-indigo-soft text-track-indigo'
								: 'bg-track-orange text-track-ink',
						)}
					>
						<span>#{truck.number}</span>
					</div>
					<div className="flex-1 min-w-0">
						<div className="flex items-center justify-between gap-x-3 gap-y-1 flex-wrap min-h-[30px]">
							<div className="flex items-center gap-2 min-w-0">
								<span className="font-semibold text-sm text-track-ink whitespace-nowrap">
									<T
										keyName="orderTracking.truckNumber"
										params={{ number: truck.number }}
									>
										{'Truck #{number}'}
									</T>
								</span>
								<span
									className={cn(
										'inline-flex items-center box-border h-[24px] px-[10px] rounded-full font-semibold whitespace-nowrap text-xs',
										!assigned
											? 'bg-track-orange text-track-ink'
											: idle
											? 'bg-track-surface text-track-slate'
											: complete
											? 'bg-primary-violet text-white'
											: 'bg-track-amber text-track-amber-ink',
									)}
								>
									{assigned
										? summary
										: t('orderTracking.notAssigned', 'Not assigned')}
								</span>
							</div>
							{assigned ? (
								<div className="flex items-center gap-2 flex-none text-sm text-track-muted whitespace-nowrap">
									<span
										className={cn(
											'w-2 h-2 rounded-full flex-none',
											moving ? 'bg-track-teal' : 'bg-track-line-strong',
										)}
									/>
									<span className={cn(moving && 'text-track-body font-medium')}>
										{moving
											? t('orderTracking.moving', 'Moving {speed} km/h', {
													speed: truck.speedKmh,
											  })
											: shortStatus}
									</span>
									{showEta ? (
										<>
											<span>·</span>
											<span>
												<T keyName="orderTracking.eta">ETA</T>{' '}
												<b className="font-semibold text-track-ink">
													{formatTime(liveDeliveryEta ?? truck.eta?.from)}
												</b>
											</span>
										</>
									) : null}
								</div>
							) : null}
						</div>
						<div className="text-[12.5px] min-w-0 whitespace-nowrap overflow-hidden text-ellipsis">
							{assigned ? (
								<span className="text-track-body font-medium">
									{truckIdentity(truck)}
								</span>
							) : collapsible ? (
								<span className="text-track-muted">
									<T keyName="orderTracking.routeAvailableAfterAssignment">
										Route available once a truck is assigned
									</T>
								</span>
							) : null}
						</div>
					</div>
					{selectable ? (
						<span
							className={cn(
								'flex flex-none transition-transform duration-300 ease-[cubic-bezier(0.2,0,0.2,1)]',
								isOpen && 'rotate-180',
							)}
						>
							<ChevronDownIcon color="#484880" width={10} height={6} />
						</span>
					) : null}
				</div>

				<div className="flex gap-2">
					{TRACKING_STEPS.map((step, index) => (
						<span
							key={step.key}
							className={cn(
								'flex-1 h-2 rounded-full transition-colors duration-300',
								!assigned
									? 'bg-track-surface'
									: index < current
									? 'bg-track-indigo'
									: index === current
									? 'bg-track-amber'
									: 'bg-track-line',
							)}
						/>
					))}
				</div>
			</button>

			<AnimatePresence initial={false}>
				{isOpen ? (
					<motion.div
						key="steps"
						initial={{ height: 0, opacity: 0 }}
						animate={{ height: 'auto', opacity: 1 }}
						exit={{ height: 0, opacity: 0 }}
						transition={TRACKING_DRAWER_TRANSITION}
						className="overflow-hidden"
					>
						<motion.div
							initial={{ y: -8 }}
							animate={{ y: 0 }}
							exit={{ y: -8 }}
							transition={TRACKING_DRAWER_TRANSITION}
						>
							<TruckSteps
								steps={steps}
								onOpenDocuments={
									onOpenDocuments ? () => onOpenDocuments(truck.id) : undefined
								}
							/>
						</motion.div>
					</motion.div>
				) : null}
			</AnimatePresence>
		</div>
	);
};

export default TruckRow;
